import { DUNNING_GRACE_DAYS } from '../constants/plans.js';
import type { SubscriptionStatus } from '../plans/entitlements.js';

/**
 * THE DUNNING STATE MACHINE — what happens to a shop between a card failing and it paying.
 *
 * Written as an explicit transition table rather than as conditions scattered through the
 * webhook handler, because this is the part of the product that decides whether somebody can
 * book an order this morning. A rule that lives in four `if` statements is a rule nobody can
 * read, and the first thing it does when it goes wrong is lock a paying shop out of its own
 * order book. Here the whole policy is one table, and `dunning.test.ts` walks every cell.
 *
 * The states are the subscription statuses task 23 already defined — this file does not invent
 * a parallel vocabulary — and GRACE is not among them on purpose. Grace is not a state; it is
 * the first {@link DUNNING_GRACE_DAYS} days of `past_due`, during which the shop keeps full
 * access. Modelling it as its own status would mean something had to move the shop out of it on
 * a timer, and a shop's ability to work would then depend on a cron job having run.
 * `subscriptionAccess()` computes it from the clock instead, so a missed job cannot lock anyone
 * out and a restored one cannot let anyone in twice.
 *
 * The one rule that outranks every transition here: NO DATA IS EVER DELETED. Read-only means
 * read-only — everything visible, everything exportable, nothing new. It is decent, and it is
 * also the only version of this that gets a shop to come back and pay.
 */

/** What the machine can be told. Each one comes from a verified webhook or an owner's action. */
export const DUNNING_EVENTS = [
  /** The mandate is live and the first charge cleared. */
  'subscription_activated',
  /** A renewal was collected. */
  'payment_succeeded',
  /** A charge was attempted and refused. */
  'payment_failed',
  /** The grace window ran out with nothing collected. */
  'grace_expired',
  /** The owner cancelled, or the gateway reports the subscription ended. */
  'cancelled',
  /** A cancelled or read-only shop chose a plan again and paid. */
  'resumed',
] as const;
export type DunningEvent = (typeof DUNNING_EVENTS)[number];

export interface DunningTransition {
  readonly to: SubscriptionStatus;
  /** Whether the paid period end moves forward. Only a successful collection does that. */
  readonly extendsPeriod: boolean;
  /** The owner is told, in the app and over WhatsApp. */
  readonly notify: 'none' | 'payment_failed' | 'read_only' | 'recovered';
  /** Why, in the words that go in the audit log. */
  readonly reason: string;
}

/**
 * The table. A missing cell means the event does not apply in that state and is IGNORED rather
 * than being an error — webhooks arrive out of order, and a `payment_succeeded` landing after
 * the shop already cancelled is a duplicate, not a crisis.
 */
const TRANSITIONS: Readonly<
  Record<SubscriptionStatus, Partial<Record<DunningEvent, DunningTransition>>>
> = Object.freeze({
  trialing: {
    subscription_activated: {
      to: 'active',
      extendsPeriod: true,
      notify: 'none',
      reason: 'Mandate authorised and the first charge cleared',
    },
    payment_succeeded: {
      to: 'active',
      extendsPeriod: true,
      notify: 'none',
      reason: 'Paid during the trial',
    },
    /**
     * A failure during the trial does NOT move the shop to past_due. The trial has not ended,
     * so nothing is owed yet; treating a failed first mandate as a missed payment would put a
     * shop that still has nine free days left into dunning.
     */
    cancelled: {
      to: 'cancelled',
      extendsPeriod: false,
      notify: 'none',
      reason: 'Cancelled before the trial ended',
    },
  },
  active: {
    payment_succeeded: {
      to: 'active',
      extendsPeriod: true,
      notify: 'none',
      reason: 'Renewal collected',
    },
    payment_failed: {
      to: 'past_due',
      extendsPeriod: false,
      notify: 'payment_failed',
      reason: 'A renewal charge was refused',
    },
    cancelled: {
      to: 'cancelled',
      extendsPeriod: false,
      notify: 'none',
      reason: 'Cancelled by the shop',
    },
  },
  past_due: {
    /** Paying during grace puts the shop straight back, with no interruption at all. */
    payment_succeeded: {
      to: 'active',
      extendsPeriod: true,
      notify: 'recovered',
      reason: 'Payment recovered',
    },
    /** A second failure inside the same window is not a new event for the shop. */
    payment_failed: {
      to: 'past_due',
      extendsPeriod: false,
      notify: 'payment_failed',
      reason: 'A retry was refused',
    },
    grace_expired: {
      to: 'read_only',
      extendsPeriod: false,
      notify: 'read_only',
      reason: 'The grace period ended with nothing collected',
    },
    cancelled: {
      to: 'cancelled',
      extendsPeriod: false,
      notify: 'none',
      reason: 'Cancelled while payment was outstanding',
    },
  },
  read_only: {
    payment_succeeded: {
      to: 'active',
      extendsPeriod: true,
      notify: 'recovered',
      reason: 'Paid after going read-only',
    },
    resumed: {
      to: 'active',
      extendsPeriod: true,
      notify: 'recovered',
      reason: 'Subscribed again',
    },
    cancelled: {
      to: 'cancelled',
      extendsPeriod: false,
      notify: 'none',
      reason: 'Cancelled while read-only',
    },
  },
  cancelled: {
    /**
     * A cancelled shop can come back, and comes back to everything it had. This is the
     * transition the "no data is ever deleted" rule exists for.
     */
    resumed: {
      to: 'active',
      extendsPeriod: true,
      notify: 'recovered',
      reason: 'Subscribed again after cancelling',
    },
    subscription_activated: {
      to: 'active',
      extendsPeriod: true,
      notify: 'recovered',
      reason: 'A new mandate was authorised',
    },
  },
});

/**
 * What `event` does to a shop in `state`, or `null` when it does nothing.
 *
 * Pure, so the same function decides in the webhook handler, in the reconciliation job and in
 * the test that enumerates every combination.
 */
export function dunningTransition(
  state: SubscriptionStatus,
  event: DunningEvent,
): DunningTransition | null {
  return TRANSITIONS[state][event] ?? null;
}

/** The state after `event`, unchanged when the event does not apply. */
export function applyDunningEvent(
  state: SubscriptionStatus,
  event: DunningEvent,
): SubscriptionStatus {
  return dunningTransition(state, event)?.to ?? state;
}

/** Every state the machine can be in — for the exhaustive test, and for the admin view. */
export const DUNNING_STATES: readonly SubscriptionStatus[] = Object.freeze(
  Object.keys(TRANSITIONS) as SubscriptionStatus[],
);

/**
 * When a past-due shop stops being able to write.
 *
 * Derived from the period end rather than stored, so it agrees with `subscriptionAccess()` by
 * construction — the screen counting down and the gate refusing the write read the same number
 * from the same place.
 */
export function readOnlyFrom(currentPeriodEndsAt: Date): Date {
  return new Date(currentPeriodEndsAt.getTime() + DUNNING_GRACE_DAYS * 86_400_000);
}

/**
 * The retry schedule, in days after the failure.
 *
 * Four attempts across the grace window, front-loaded: most refusals are a temporary balance
 * problem, and the one that clears does so within a day or two of payday. The last attempt
 * lands before the window closes so the shop is never told "we tried" after they have already
 * lost access.
 */
export const DUNNING_RETRY_DAYS: readonly number[] = Object.freeze([1, 3, 5, 7]);

/** When to try again after `attempt` failures, or `null` when the schedule is exhausted. */
export function nextRetryAt(failedAt: Date, attempt: number): Date | null {
  const days = DUNNING_RETRY_DAYS[attempt];
  return days === undefined ? null : new Date(failedAt.getTime() + days * 86_400_000);
}

/**
 * When to nudge a shop about a trial that is running out.
 *
 * Seven days out is information; three days is a reminder; one day is a warning, and the day
 * itself is the last honest chance to act. Anything more often is a product nagging somebody
 * who has not decided yet, which is how a trial ends in a cancellation rather than a payment.
 */
export const TRIAL_REMINDER_DAYS: readonly number[] = Object.freeze([7, 3, 1, 0]);

/** True when a trial with `daysRemaining` left should be nudged today. */
export function shouldRemindAboutTrial(daysRemaining: number): boolean {
  return TRIAL_REMINDER_DAYS.includes(daysRemaining);
}

/** How loudly the trial banner speaks. The last three days are not the first eleven. */
export function trialUrgency(daysRemaining: number | null): 'none' | 'calm' | 'soon' | 'urgent' {
  if (daysRemaining === null) return 'none';
  if (daysRemaining <= 1) return 'urgent';
  if (daysRemaining <= 3) return 'soon';
  return 'calm';
}
